<script lang="ts">
  import { post } from '../api';
  import { chat } from '../store';
  import { formatListTime, PLATFORM_LABEL } from '../ui';
  import Avatar from './Avatar.svelte';
  import Icon from './Icon.svelte';

  interface Props {
    onNewRoom: () => void;
    onJoin: () => void;
  }

  let { onNewRoom, onJoin }: Props = $props();
  let query = $state('');

  const conversations = $derived($chat.conversations);
  const filtered = $derived.by(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return conversations;
    return conversations.filter(
      (item) =>
        item.title.toLowerCase().includes(needle) ||
        (item.preview ?? '').toLowerCase().includes(needle) ||
        PLATFORM_LABEL[item.platform].toLowerCase().includes(needle),
    );
  });

  function openConversation(id: string) {
    chat.setActive(id);
    chat.markRead(id);
    void post(`/api/conversations/${encodeURIComponent(id)}/read`).catch(() => {});
  }
</script>

<aside class="sidebar">
  <header class="sidebar-head">
    <div class="brand">
      <div class="brand-mark">C</div>
      <div>
        <strong>Chatsi</strong>
        <span class={`conn ${$chat.connected ? 'on' : 'off'}`}>{$chat.connected ? 'live' : 'offline'}</span>
      </div>
    </div>
    <button class="icon-btn" onclick={() => chat.toggleSettings(true)} title="Settings" aria-label="Settings">
      <Icon name="settings" />
    </button>
  </header>

  <div class="search">
    <Icon name="search" size={18} />
    <input bind:value={query} placeholder="Search chats" aria-label="Search chats" />
  </div>

  <div class="quick">
    <button class="quick-btn" onclick={onNewRoom}><Icon name="plus" size={16} /> Mesh room</button>
    <button class="quick-btn" onclick={onJoin}><Icon name="link" size={16} /> Join</button>
  </div>

  <div class="list-head">
    <span>Chats</span>
    <span class="list-count">{filtered.length}</span>
  </div>

  <ul class="conv-list">
    {#each filtered as conversation (conversation.id)}
      <li>
        <button
          class={`conv ${$chat.activeId === conversation.id ? 'active' : ''}`}
          onclick={() => openConversation(conversation.id)}
        >
          <Avatar name={conversation.title} url={conversation.avatar} platform={conversation.platform} />
          <div class="conv-body">
            <div class="conv-row">
              <span class="conv-title">{conversation.title}</span>
              <span class="conv-time">{formatListTime(conversation.lastMessageAt)}</span>
            </div>
            <div class="conv-row">
              <span class="conv-preview">{conversation.preview ?? PLATFORM_LABEL[conversation.platform]}</span>
              {#if conversation.unread > 0}
                <span class="badge">{conversation.unread > 99 ? '99+' : conversation.unread}</span>
              {/if}
            </div>
          </div>
        </button>
      </li>
    {/each}
    {#if !filtered.length}
      <li class="empty-list">No chats yet. Connect Telegram or Discord, or create a mesh room.</li>
    {/if}
  </ul>

  <button class="identity-chip" onclick={() => chat.toggleSettings(true)} title="Change your display name">
    <span class="identity-name">{$chat.me.name}</span>
    {#if !$chat.me.named}
      <span class="identity-cta">Set name</span>
    {/if}
  </button>

  <footer class="sidebar-foot">
    <span class={`pill ${$chat.capabilities?.telegram.enabled ? 'ok' : ''}`}>TG</span>
    <span class={`pill ${$chat.capabilities?.discord.enabled ? 'ok' : ''}`}>DC</span>
    <span class={`pill ${$chat.capabilities?.mesh.enabled ? 'ok' : ''}`}>
      P2P {$chat.capabilities?.mesh.peers ?? 0}
    </span>
    <span class={`pill ${$chat.capabilities?.livekit.enabled ? 'ok' : ''}`}>Calls</span>
  </footer>
</aside>
